import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Badge, List, ListItem, Tag, Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import {
  BrandOrderingPlatformFilter,
  BrandOrderingPlatformsOrderBy,
} from "__generated__/gql/graphql";
import { customerPage, CUSTOMER_WAREHOUSE_PAGE } from "utils/links";
import FullPageTable, {
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import IsCustomBadge from "components/Shared/OrderingPlatformDirectory/IsCustomBadge";
import Link from "components/Shared/Link";
import { assertPresence } from "utils/assertions";
import { formatJustifiedDateTimeCell } from "components/Shared/ChakraReactTable/formatters";
import { NA } from "components/Shared/EmptyPlaceholder";
import CopyableCode from "components/Shared/CopyableCode";
import { deliveryMethodLabelsByType } from "components/Shared/DeliveryMethodButtons";
import SyncIndicator from "components/Shared/SyncIndicator";

const fragment = gql(/* GraphQL */ `
  fragment BrandCustomersTableRow on BrandOrderingPlatform {
    id
    supplierName
    supplierCode
    deliveryMethod
    createdAt
    analyticsSyncDetails {
      ...SyncIndicator
    }
    orderingPlatform {
      id
      name
      ...CustomerIsCustomBadge
      ...CustomerAnalyticsSupport
    }
    brandDistributionCentersList(
      orderBy: DISTRIBUTION_CENTER__NAME_ASC
      filter: {
        isArchived: { equalTo: false }
        distributionCenter: {
          name: { isNull: false }
          isArchived: { equalTo: false }
        }
      }
    ) {
      id
      analyticsSyncDetails {
        ...SyncIndicator
      }
      distributionCenter {
        id
        name
      }
    }
  }
`);

const query = gql(/* GraphQL */ `
  query AllCustomersTable(
    $brandId: UUID = null
    $filter: BrandOrderingPlatformFilter
    $orderBy: [BrandOrderingPlatformsOrderBy!] = [ORDERING_PLATFORM__NAME_ASC]
    $limit: Int
    $offset: Int
  ) {
    brandOrderingPlatforms(
      condition: { brandId: $brandId }
      filter: $filter
      orderBy: $orderBy
      first: $limit
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        ...BrandCustomersTableRow
      }
    }
  }
`);

type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

const columns = [
  col.accessor("orderingPlatform.name", {
    id: "name",
    header: "Name",
    cell: (cell) => (
      <Link
        href={customerPage(cell.row.original.orderingPlatform?.id)}
        lineHeight="normal"
        display="flex"
        alignItems="center"
        gap={2}
      >
        {cell.getValue()}
        <SyncIndicator
          analyticsSyncDetails={cell.row.original.analyticsSyncDetails}
          ml="auto"
        />
      </Link>
    ),
    size: 225,
  }),
  col.accessor("supplierName", {
    header: "Supplier Name",
    cell: (cell) => cell.getValue() ?? <NA />,
    size: 175,
  }),
  col.accessor("supplierCode", {
    header: "Supplier Code",
    cell: (cell) => {
      const code = cell.getValue();
      if (!code) return <NA />;
      return <CopyableCode code={code} isTruncated maxW="100%" />;
    },
    size: 160,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("deliveryMethod", {
    header: "Delivery Method",
    cell: (cell) => {
      const deliveryMethod = cell.getValue();
      if (!deliveryMethod) return <NA />;
      return (
        <Badge variant="outline">
          {deliveryMethodLabelsByType[deliveryMethod]}
        </Badge>
      );
    },
    size: 175,
    meta: { props: { textAlign: "center" } },
  }),
  col.display({
    id: "isCustom",
    header: "Custom?",
    cell: (cell) =>
      cell.row.original.orderingPlatform && (
        <IsCustomBadge
          orderingPlatform={cell.row.original.orderingPlatform}
          isTruncated
          maxW="100%"
        />
      ),
    size: 95,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("brandDistributionCentersList", {
    id: "warehouses",
    header: "Warehouses",
    cell: (cell) => {
      const bop = cell.row.original;
      assertPresence(bop.orderingPlatform);
      const orderingPlatformId = bop.orderingPlatform.id;

      if (!cell.row.original.brandDistributionCentersList?.length) {
        return <NA />;
      }

      return (
        <List display="flex" flexWrap="wrap" m="-.125rem">
          {cell.row.original?.brandDistributionCentersList?.map((bdc) => {
            assertPresence(bdc.distributionCenter);
            return (
              <ListItem key={bdc.id}>
                <Tag
                  as={Link}
                  href={{
                    pathname: CUSTOMER_WAREHOUSE_PAGE,
                    query: {
                      orderingPlatformId,
                      distributionCenterId: bdc.distributionCenter.id,
                    },
                  }}
                  fontSize="xs"
                  minHeight="1em"
                  lineHeight="normal"
                  py=".25em"
                  m=".125rem"
                >
                  {bdc.distributionCenter.name}
                  <SyncIndicator
                    analyticsSyncDetails={bdc.analyticsSyncDetails}
                    ml={0}
                    mr={-1}
                  />
                </Tag>
              </ListItem>
            );
          })}
        </List>
      );
    },
    enableSorting: false,
    minSize: 450,
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: formatJustifiedDateTimeCell,
    size: 200,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { textAlign: "right", fontSize: "sm" },
    },
  }),
];

const columnPinning: ColumnPinningState = { left: ["name"] };

const orderByEnumsById: GQLOrderByEnumsById<BrandOrderingPlatformsOrderBy> = {
  name: [
    BrandOrderingPlatformsOrderBy.ORDERING_PLATFORM__NAME_ASC,
    BrandOrderingPlatformsOrderBy.ORDERING_PLATFORM__NAME_DESC,
  ],
  supplierName: [
    BrandOrderingPlatformsOrderBy.SUPPLIER_NAME_ASC,
    BrandOrderingPlatformsOrderBy.SUPPLIER_NAME_DESC,
  ],
  supplierCode: [
    BrandOrderingPlatformsOrderBy.SUPPLIER_CODE_ASC,
    BrandOrderingPlatformsOrderBy.SUPPLIER_CODE_DESC,
  ],
  deliveryMethod: [
    BrandOrderingPlatformsOrderBy.DELIVERY_METHOD_ASC,
    BrandOrderingPlatformsOrderBy.DELIVERY_METHOD_DESC,
  ],
  createdAt: [
    BrandOrderingPlatformsOrderBy.CREATED_AT_ASC,
    BrandOrderingPlatformsOrderBy.CREATED_AT_DESC,
  ],
};

export type Props = {
  filter?: BrandOrderingPlatformFilter;
  isReady?: boolean;
};

export default function CustomersTable({ filter, isReady = true }: Props) {
  const brandId = useCurrentBrandId();
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: 50 });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "name", desc: false }],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useBrandQuery(query, {
    skip: !brandId || isPendingFilterReset || isPendingSortingReset || !isReady,
    variables: { brandId, filter, orderBy, ...limitOffset },
    fetchPolicy: "cache-and-network",
  });

  const { nodes: customers, totalCount } =
    rawData?.brandOrderingPlatforms || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, customers)),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    {
      tableProps: { fontSize: "sm" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  return (
    <FullPageTable
      {...{ tableInstance }}
      {...tableProps}
      isLoading={isLoading || typeof window === "undefined" || !isReady}
      totalCount={totalCount}
      resultTypeLabel="customers"
      onRefresh={refetch}
      noRowsPlaceholder={
        <Text>
          No customers {filter ? "found" : "have been added to your account"}.
        </Text>
      }
    />
  );
}
